import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authApi } from '@/api';
import { platformConfigApi, type PlatformConfig } from '@/api/platformConfig.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, { message: 'Must match', path: ['confirmPassword'] });

type PasswordForm = z.infer<typeof passwordSchema>;

export function MasterSettingsPage() {
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'platform' | 'password'>('platform');

  const [form, setForm] = useState({
    frontendUrl: '',
    corsOrigins: '',
    mfaIssuer: '',
    ssoEnabled: false,
    ssoCallbackBaseUrl: '',
    ssoGoogleEnabled: false,
    ssoGoogleClientId: '',
    ssoGoogleClientSecret: '',
    ssoMicrosoftEnabled: false,
    ssoMicrosoftClientId: '',
    ssoMicrosoftClientSecret: '',
    mailFrom: '',
    mailFromName: '',
    mailHost: '',
    mailPort: 587,
    mailUsername: '',
    mailPassword: '',
    rateLimitEnabled: true,
    rateLimitMax: 500,
    rateLimitAuthMax: 30,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformConfigApi.get();
      setConfig(data);
      setForm({
        frontendUrl: data.general.frontendUrl,
        corsOrigins: data.general.corsOrigins,
        mfaIssuer: data.security.mfaIssuer,
        ssoEnabled: data.sso.enabled,
        ssoCallbackBaseUrl: data.sso.callbackBaseUrl,
        ssoGoogleEnabled: data.sso.google.enabled,
        ssoGoogleClientId: data.sso.google.clientId ?? '',
        ssoGoogleClientSecret: '',
        ssoMicrosoftEnabled: data.sso.microsoft.enabled,
        ssoMicrosoftClientId: data.sso.microsoft.clientId ?? '',
        ssoMicrosoftClientSecret: '',
        mailFrom: data.mail.from,
        mailFromName: data.mail.fromName,
        mailHost: data.mail.host ?? '',
        mailPort: data.mail.port ?? 587,
        mailUsername: data.mail.username ?? '',
        mailPassword: '',
        rateLimitEnabled: data.rateLimit.enabled,
        rateLimitMax: data.rateLimit.maxRequestsPerMinute,
        rateLimitAuthMax: data.rateLimit.authMaxRequestsPerMinute,
      });
    } catch (err) {
      showApiError(err, 'Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const savePlatform = async () => {
    setSaving(true);
    try {
      const updated = await platformConfigApi.save({
        frontendUrl: form.frontendUrl,
        corsOrigins: form.corsOrigins,
        mfaIssuer: form.mfaIssuer,
        ssoEnabled: form.ssoEnabled,
        ssoCallbackBaseUrl: form.ssoCallbackBaseUrl,
        ssoGoogleEnabled: form.ssoGoogleEnabled,
        ssoGoogleClientId: form.ssoGoogleClientId,
        ssoGoogleClientSecret:
          form.ssoGoogleClientSecret || undefined,
        ssoMicrosoftEnabled: form.ssoMicrosoftEnabled,
        ssoMicrosoftClientId: form.ssoMicrosoftClientId,
        ssoMicrosoftClientSecret:
          form.ssoMicrosoftClientSecret || undefined,
        mailFrom: form.mailFrom,
        mailFromName: form.mailFromName,
        mailHost: form.mailHost,
        mailPort: form.mailPort,
        mailUsername: form.mailUsername,
        mailPassword: form.mailPassword || undefined,
        rateLimitEnabled: form.rateLimitEnabled,
        rateLimitMax: form.rateLimitMax,
        rateLimitAuthMax: form.rateLimitAuthMax,
      });
      setConfig(updated);
      setForm((f) => ({ ...f, ssoGoogleClientSecret: '', ssoMicrosoftClientSecret: '', mailPassword: '' }));
      notify.success('Platform settings saved — SSO, mail, and rate limits apply immediately');
    } catch (err) {
      showApiError(err, 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordForm>({ resolver: zodResolver(passwordSchema) });

  const [pwdLoading, setPwdLoading] = useState(false);
  const onPassword = async (data: PasswordForm) => {
    setPwdLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      notify.success('Password changed');
      reset();
    } catch (err) {
      showApiError(err, 'Failed to change password');
    } finally {
      setPwdLoading(false);
    }
  };

  const secretPlaceholder = (configured: boolean) =>
    configured ? '••••••••  (leave blank to keep)' : 'Enter secret';

  return (
    <div>
      <PageHeader title="Settings" breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Settings' }]} />

      <div className="mb-4 flex gap-2 border-b border-slate-200 dark:border-slate-700">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium ${tab === 'platform' ? 'border-b-2 border-primary-600 text-primary-700' : 'text-slate-500'}`}
          onClick={() => setTab('platform')}
        >
          Platform config
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium ${tab === 'password' ? 'border-b-2 border-primary-600 text-primary-700' : 'text-slate-500'}`}
          onClick={() => setTab('password')}
        >
          Your password
        </button>
      </div>

      {tab === 'password' && (
        <div className="card max-w-lg p-6">
          <h3 className="font-semibold">Change password</h3>
          <form onSubmit={handleSubmit(onPassword)} className="mt-4 space-y-4">
            <Input label="Current password" type="password" {...register('currentPassword')} error={errors.currentPassword?.message} />
            <Input label="New password" type="password" {...register('newPassword')} error={errors.newPassword?.message} />
            <Input label="Confirm password" type="password" {...register('confirmPassword')} error={errors.confirmPassword?.message} />
            <Button type="submit" loading={pwdLoading}>
              Update password
            </Button>
          </form>
        </div>
      )}

      {tab === 'platform' && (
        <div className="space-y-6">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Values saved here override <code className="text-xs">application.yml</code> / environment for SSO, mail, CORS, and rate
            limits. Secrets are never shown after save.
          </p>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : (
            <>
              <section className="card p-6">
                <h3 className="font-semibold">General</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Input
                    label="Frontend URL"
                    value={form.frontendUrl}
                    onChange={(e) => setForm({ ...form, frontendUrl: e.target.value })}
                    hint={config?.general.frontendUrlFromDb ? 'Saved in database' : undefined}
                  />
                  <div className="md:col-span-2">
                    <Input
                      label="CORS allowed origins (comma-separated)"
                      value={form.corsOrigins}
                      onChange={(e) => setForm({ ...form, corsOrigins: e.target.value })}
                    />
                  </div>
                  <Input
                    label="MFA issuer name"
                    value={form.mfaIssuer}
                    onChange={(e) => setForm({ ...form, mfaIssuer: e.target.value })}
                  />
                </div>
              </section>

              <section className="card p-6">
                <h3 className="font-semibold">SSO (Google & Microsoft)</h3>
                <label className="mt-4 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.ssoEnabled}
                    onChange={(e) => setForm({ ...form, ssoEnabled: e.target.checked })}
                  />
                  Enable SSO
                </label>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Input
                      label="OAuth callback base URL"
                      placeholder="https://api.yourdomain.com/api"
                      value={form.ssoCallbackBaseUrl}
                      onChange={(e) => setForm({ ...form, ssoCallbackBaseUrl: e.target.value })}
                    />
                  </div>
                  <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={form.ssoGoogleEnabled}
                        onChange={(e) => setForm({ ...form, ssoGoogleEnabled: e.target.checked })}
                      />
                      Google
                    </label>
                    <Input
                      label="Client ID"
                      className="mt-2"
                      value={form.ssoGoogleClientId}
                      onChange={(e) => setForm({ ...form, ssoGoogleClientId: e.target.value })}
                    />
                    <Input
                      label="Client secret"
                      className="mt-2"
                      type="password"
                      placeholder={secretPlaceholder(config?.sso.google.clientSecretConfigured ?? false)}
                      value={form.ssoGoogleClientSecret}
                      onChange={(e) => setForm({ ...form, ssoGoogleClientSecret: e.target.value })}
                    />
                  </div>
                  <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={form.ssoMicrosoftEnabled}
                        onChange={(e) => setForm({ ...form, ssoMicrosoftEnabled: e.target.checked })}
                      />
                      Microsoft
                    </label>
                    <Input
                      label="Client ID"
                      className="mt-2"
                      value={form.ssoMicrosoftClientId}
                      onChange={(e) => setForm({ ...form, ssoMicrosoftClientId: e.target.value })}
                    />
                    <Input
                      label="Client secret"
                      className="mt-2"
                      type="password"
                      placeholder={secretPlaceholder(config?.sso.microsoft.clientSecretConfigured ?? false)}
                      value={form.ssoMicrosoftClientSecret}
                      onChange={(e) => setForm({ ...form, ssoMicrosoftClientSecret: e.target.value })}
                    />
                  </div>
                </div>
              </section>

              <section className="card p-6">
                <h3 className="font-semibold">Email (SMTP)</h3>
                {config?.mail.smtpReady && (
                  <p className="mt-1 text-xs text-green-600">SMTP appears configured</p>
                )}
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Input
                    label="From email"
                    type="email"
                    value={form.mailFrom}
                    onChange={(e) => setForm({ ...form, mailFrom: e.target.value })}
                  />
                  <Input
                    label="From name"
                    value={form.mailFromName}
                    onChange={(e) => setForm({ ...form, mailFromName: e.target.value })}
                  />
                  <Input
                    label="SMTP host"
                    value={form.mailHost}
                    onChange={(e) => setForm({ ...form, mailHost: e.target.value })}
                  />
                  <Input
                    label="Port"
                    type="number"
                    value={form.mailPort}
                    onChange={(e) => setForm({ ...form, mailPort: Number(e.target.value) })}
                  />
                  <Input
                    label="SMTP username"
                    value={form.mailUsername}
                    onChange={(e) => setForm({ ...form, mailUsername: e.target.value })}
                  />
                  <Input
                    label="SMTP password"
                    type="password"
                    placeholder={secretPlaceholder(config?.mail.passwordConfigured ?? false)}
                    value={form.mailPassword}
                    onChange={(e) => setForm({ ...form, mailPassword: e.target.value })}
                  />
                </div>
              </section>

              <section className="card p-6">
                <h3 className="font-semibold">Rate limiting</h3>
                <label className="mt-4 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.rateLimitEnabled}
                    onChange={(e) => setForm({ ...form, rateLimitEnabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Input
                    label="Max requests / minute"
                    type="number"
                    value={form.rateLimitMax}
                    onChange={(e) => setForm({ ...form, rateLimitMax: Number(e.target.value) })}
                  />
                  <Input
                    label="Auth endpoints max / minute"
                    type="number"
                    value={form.rateLimitAuthMax}
                    onChange={(e) => setForm({ ...form, rateLimitAuthMax: Number(e.target.value) })}
                  />
                </div>
              </section>

              <div className="flex gap-3">
                <Button onClick={() => void savePlatform()} loading={saving}>
                  Save platform settings
                </Button>
                <Button variant="secondary" onClick={() => void load()}>
                  Reload
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
